import { computed, ref } from 'vue'

/**
 * Installation de Centime sur l'écran d'accueil (PWA).
 * - Android et ordinateurs Chromium : l'invite native est capturée au chargement et rejouée
 *   au clic sur « Installer » ;
 * - iPhone et iPad : Safari n'a pas d'invite, on montre le geste (Partager, puis « Sur l'écran
 *   d'accueil ») ;
 * - déjà installée : on propose simplement d'ouvrir l'application.
 */

interface InstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

const deferred = ref<InstallPromptEvent | null>(null)
const installed = ref(false)

function standalone(): boolean {
  if (typeof window === 'undefined') return false
  const nav = navigator as Navigator & { standalone?: boolean }
  return window.matchMedia('(display-mode: standalone)').matches || nav.standalone === true
}

/** iPhone, iPad (y compris iPadOS qui se présente comme un Mac tactile). */
function isApple(): boolean {
  if (typeof navigator === 'undefined') return false
  const ua = navigator.userAgent
  return /iPhone|iPad|iPod/.test(ua) || (ua.includes('Macintosh') && navigator.maxTouchPoints > 1)
}

/** À appeler une fois, au démarrage : l'événement d'installation arrive très tôt. */
export function listenForInstall(): void {
  installed.value = standalone()
  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault()
    deferred.value = event as InstallPromptEvent
  })
  window.addEventListener('appinstalled', () => {
    deferred.value = null
    installed.value = true
  })
}

export type InstallMode = 'installed' | 'prompt' | 'ios' | 'manual'

export function useInstall() {
  const mode = computed<InstallMode>(() => {
    if (installed.value) return 'installed'
    if (deferred.value) return 'prompt'
    if (isApple()) return 'ios'
    return 'manual'
  })

  /** Ouvre l'invite native. Renvoie `false` si elle n'est pas disponible (afficher l'aide). */
  async function install(): Promise<boolean> {
    const event = deferred.value
    if (!event) return false
    await event.prompt()
    const { outcome } = await event.userChoice
    deferred.value = null
    if (outcome === 'accepted') installed.value = true
    return true
  }

  return { mode, install }
}

/**
 * Demande au navigateur de ne pas effacer les données du site (session comprise) quand
 * l'espace manque. Sans effet visible ; refusé silencieusement si le navigateur décide seul.
 */
export async function requestPersistentStorage(): Promise<void> {
  try {
    if (!(await navigator.storage.persisted())) {
      await navigator.storage.persist()
    }
  } catch {
    // Non pris en charge : la session reste en stockage local classique.
  }
}
